import {
    Icon,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { format, fromUnixTime } from 'date-fns';
import React from 'react';

import { useTaxonomy } from 'app/hooks/use-taxonomy';

import { MidStats } from 'app/model/mid-stats';
import { SeamRequest } from 'app/model/seam-request';
import { messagingService } from 'app/services/messaging.service';
import { seamRequestService } from 'app/services/seam-request.service';

interface SeamRequestStatsProps {
    request: SeamRequest;
}

export const SeamRequestStats: React.FunctionComponent<SeamRequestStatsProps> = (props) => {
    const { currencySymbol } = useTaxonomy();
    const recompute = () => {
        seamRequestService.runStats(props.request).subscribe(() => {
            messagingService.showSuccess('Recompute Scheduled. Check back in a bit for updated stats.');
        });
    };

    const generateRow = (type: string, stats: MidStats, sign = '') => (
        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }} key={type}>
            <TableCell component='th' scope='row'>
                {type}
            </TableCell>
            <TableCell align='right'>
                {sign}
                {stats.midCount.toLocaleString()}
            </TableCell>
            <TableCell align='right'>
                {sign}
                {stats.trxCount.toLocaleString()}
            </TableCell>
            <TableCell align='right'>
                {currencySymbol} {sign}
                {Number(stats.totalSpend).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </TableCell>
        </TableRow>
    );

    const totalRow = (initial: MidStats, diff: MidStats) => {
        const operation: (a: number, b: number) => number = props.request.correction
            ? (a, b) => a - b
            : (a, b) => a + b;

        return (
            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }} key='after'>
                <TableCell component='th' scope='row'>
                    After
                </TableCell>
                <TableCell align='right'>{operation(+initial.midCount, +diff.midCount).toLocaleString()}</TableCell>
                <TableCell align='right'>{operation(+initial.trxCount, +diff.trxCount).toLocaleString()}</TableCell>
                <TableCell align='right'>
                    {currencySymbol}{' '}
                    {operation(+initial.totalSpend, +diff.totalSpend).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                    })}
                </TableCell>
            </TableRow>
        );
    };

    return (
        <React.Fragment>
            <Typography variant='h6' className='mt-2'>
                Stats
                <Tooltip title='Schedule Recompute'>
                    <IconButton aria-label='schedule recompute' onClick={recompute}>
                        <Icon>update</Icon>
                    </IconButton>
                </Tooltip>
                <small>
                    Updated @{' '}
                    {0 === props.request.statUpdatedAt
                        ? '[never]'
                        : format(fromUnixTime(props.request.statUpdatedAt), "h:mm:ss a 'on' MM/dd/yyyy")}
                </small>
            </Typography>
            <TableContainer component={Paper}>
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell align='right'>Mid Count</TableCell>
                            <TableCell align='right'>Trx Count</TableCell>
                            <TableCell align='right'>Spend</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {generateRow('Before', props.request.initialStats)}
                        {generateRow('Change', props.request.diffStats, props.request.correction ? '-' : '+')}
                        {totalRow(props.request.initialStats, props.request.diffStats)}
                    </TableBody>
                </Table>
            </TableContainer>
        </React.Fragment>
    );
};
